import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * HOVER PANEL — DP60 Part 0d.
 *
 * ONE mechanism, not a third. This is the avatar menu's behaviour from
 * components/FixedBottomNav.js, which components/layout/AdminToolsSquare.js
 * already reused verbatim, lifted here so the rail's boxes use the same code
 * rather than a second copy:
 *
 *   - three phases: 'closed' | 'fading' | 'open', exposed as data-menu-phase
 *   - open on mouseenter and focus, startFade on mouseleave and blur
 *   - a 700ms fade during which the panel KEEPS pointer-events, so the pointer
 *     can travel from the trigger into the panel without it shutting.
 *     FixedBottomNav's own comment warns that making the panel
 *     pointer-events-none while fading is "the easy way to get this wrong".
 *   - click PINS it open, which is also how a tap works on a touch device
 */
export const HOVER_FADE_MS = 700;

export function useHoverPanel() {
  const [phase, setPhase] = useState('closed');
  const [pinned, setPinned] = useState(false);
  const timer = useRef(null);

  const cancelFade = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const open = useCallback(() => {
    cancelFade();
    setPhase('open');
  }, [cancelFade]);

  const startFade = useCallback(() => {
    if (pinned) return;
    cancelFade();
    setPhase('fading');
    timer.current = setTimeout(() => {
      timer.current = null;
      setPhase('closed');
    }, HOVER_FADE_MS);
  }, [pinned, cancelFade]);

  const closeNow = useCallback(() => {
    cancelFade();
    setPinned(false);
    setPhase('closed');
  }, [cancelFade]);

  const togglePin = useCallback(() => {
    cancelFade();
    setPinned((prev) => {
      const next = !prev;
      setPhase(next ? 'open' : 'closed');
      return next;
    });
  }, [cancelFade]);

  useEffect(() => cancelFade, [cancelFade]);

  return {
    phase,
    pinned,
    expanded: pinned || phase === 'open',
    dataPhase: pinned ? 'pinned' : phase,
    open,
    startFade,
    closeNow,
    togglePin,
    triggerProps: {
      onMouseEnter: open,
      onMouseLeave: startFade,
      onFocus: open,
      onBlur: startFade,
    },
  };
}

export default useHoverPanel;
